import { Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Store } from '@ngxs/store';
import { AlertController, ToastController } from '@ionic/angular';
import { CallingState, GpAsteriskService, GpAsteriskState, GpCallModel, INTERNAL_CALL_PREFIX } from 'gp-asterisk';
import { distinctUntilChanged, firstValueFrom, map, pairwise } from 'rxjs';
import { InternalCallService } from './internal-call.service';
import { SoftphoneService } from './softphone.service';
import { diagLog } from './native-session-description-handler';
import { isVoipCallAvailable, VoipCall } from './voip-call.plugin';

/**
 * Вбудований телефон, етап 2: прямі дзвінки, коли застосунок закритий / екран заблоковано.
 * Бекенд надсилає push -> система показує екран дзвінка (iOS CallKit, Android повноекранне сповіщення).
 * Тут: реєструємо токен пристрою, а на «Відповісти» відкриваємо лінію і забираємо дзвінок собі.
 */
// як часто відкритий застосунок повідомляє «я на зв'язку»
const HEARTBEAT_MS = 5 * 60 * 1000;
// нагадування про дозвіл на екран дзвінка на заблокованому телефоні (Android 14+) — не частіше ніж раз на 3 дні
const LOCK_SCREEN_HINT_KEY = 'softphone_lock_screen_hint_at';
const LOCK_SCREEN_HINT_EVERY_MS = 3 * 24 * 60 * 60 * 1000;
// скільки найдовше стежимо за дзвінком, що дзвонить (система сама прибирає його через 45 с)
const RINGING_WATCH_MS = 60 * 1000;

@Injectable({ providedIn: 'root' })
export class IncomingCallService {
  private started = false;
  // дзвінки, що зараз показані системою (ще без відповіді), — щоб прибрати їх, коли дзвінок закінчився деінде
  private ringing = new Set<string>();
  // дзвінок, на який відповіли із системного екрана (iOS: CallKit треба завершити разом із розмовою)
  private answeredChannelId: string | null = null;

  constructor(
    private softphone: SoftphoneService,
    private internalCalls: InternalCallService,
    private asteriskService: GpAsteriskService,
    private store: Store,
    private toastController: ToastController,
    private alertController: AlertController,
    private zone: NgZone
  ) {}

  private listening = false;
  private savedToken: string | null = null;

  async start(): Promise<void> {
    if (this.started || !this.softphone.isActive || !isVoipCallAvailable()) return;
    this.started = true;
    VoipCall.getToken().then(({ platform, token }) => this.saveToken(platform, token)).catch(() => {
      // iOS видає токен PushKit трохи пізніше — прийде подією 'token'
    });
    if (this.listening) return;
    this.listening = true;

    VoipCall.addListener('token', ({ platform, token }) => this.saveToken(platform, token));

    VoipCall.addListener('callIncoming', ({ channelId }) => {
      this.ringing.add(channelId);
      this.watchRinging(channelId);
      diagLog(`callIncoming ${channelId} softphone=${this.softphone.state$.value.status}`);
      // поки дзвонить — перепідключаємось до АТС, щоб після «Прийняти» не чекати
      this.softphone.prepareForCall().catch(() => {});
      // підтвердження доставки (на Android його вже надіслала нативна частина, на iPhone — лише звідси)
      if (this.savedToken) this.asteriskService.softphoneDelivered(this.savedToken, channelId).subscribe({ error: () => {} });
    });
    this.watchInternalCalls();
    this.checkLockScreenCalls();
    // відповідь забираємо з нативної частини (там вона чекає, доки JS не оживе) — так не губиться і не дублюється
    VoipCall.addListener('callAnswered', ({ channelId }) => this.zone.run(() => this.takePendingAnswer(channelId)));
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) this.zone.run(() => this.takePendingAnswer());
      if (isActive) this.heartbeat();
    });
    // поки застосунок відкритий — «у мережі» (на iPhone це єдиний сигнал: фонових перевірок там немає)
    setInterval(() => this.heartbeat(), HEARTBEAT_MS);
    // iOS: поклали трубку на системному екрані CallKit
    VoipCall.addListener('callEnded', () => this.zone.run(() => this.softphone.hangup()));

    await this.takePendingAnswer();

    // розмова в застосунку закінчилась — закриваємо й системний дзвінок (iOS CallKit).
    // Поки відповідаємо (застосунок щойно запустився і перепідключається), невдала перша спроба лінії — не кінець
    // розмови: закрий ми CallKit, iOS вимкне звук застосунку у фоні, і друга, вдала спроба буде без звуку
    this.softphone.state$.pipe(map(state => !!state.call), distinctUntilChanged(), pairwise()).subscribe(([had, has]) => {
      if (had && !has && this.answeredChannelId && !this.answering) {
        VoipCall.endCall({ channelId: this.answeredChannelId }).catch(() => {});
        this.answeredChannelId = null;
      }
    });

    // дзвінок закінчився або його прийняв інший — прибираємо системний екран дзвінка
    this.store.select(GpAsteriskState.callList).subscribe(calls => {
      for (const channelId of [...this.ringing]) {
        // внутрішніх дзвінків немає серед дзвінків клієнтів — їх закриваємо за подією InternalCall
        if (channelId.startsWith(INTERNAL_CALL_PREFIX)) continue;
        const call = (calls as GpCallModel[] | null)?.find(item => item.id === channelId);
        if (!call || call.status === CallingState.Answered) {
          this.ringing.delete(channelId);
          if (channelId !== this.answeredChannelId) VoipCall.endCall({ channelId, answeredElsewhere: !!call }).catch(() => {});
        }
      }
    });
  }

  // Поки дзвонить, раз на 2 с перевіряємо на бекенді, чи дзвінок ще актуальний: подію про скасування по WebSocket
  // застосунок міг пропустити (iOS розбудив його вже після скасування), а push «дзвінок завершено» — загубитися (Android).
  // Скасували — прибираємо екран дзвінка; відповіли деінде — теж, як «прийнято на іншому пристрої»
  private watchRinging(channelId: string): void {
    const started = Date.now();
    const check = async () => {
      if (!this.ringing.has(channelId) || channelId === this.answeredChannelId || Date.now() - started > RINGING_WATCH_MS) return;
      const result = await firstValueFrom(this.asteriskService.softphoneCallStatus(channelId)).catch(() => null);
      if (result && result.status !== 'ringing' && this.ringing.has(channelId) && channelId !== this.answeredChannelId) {
        diagLog(`ringing ${channelId} → ${result.status}: прибираємо екран дзвінка`);
        this.ringing.delete(channelId);
        VoipCall.endCall({ channelId, answeredElsewhere: result.status === 'active' }).catch(() => {});
        return;
      }
      setTimeout(check, 2000);
    };
    setTimeout(check, 1500);
  }

  private watchInternalCalls(): void {
    // колега поклав трубку або ви відповіли на іншому пристрої (SIP-телефон)
    this.internalCalls.updates$.subscribe(call => {
      if (!this.ringing.has(call.id) || call.status === 'ringing' || call.id === this.answeredChannelId) return;
      this.ringing.delete(call.id);
      VoipCall.endCall({ channelId: call.id, answeredElsewhere: call.status === 'active' }).catch(() => {});
    });
  }

  // Android 14+: без дозволу на повноекранні сповіщення на заблокованому екрані лише звичайне сповіщення —
  // звук є, а взяти слухавку ніде. Пояснюємо й ведемо в налаштування
  private async checkLockScreenCalls(): Promise<void> {
    if (Capacitor.getPlatform() !== 'android') return;
    const { granted } = await VoipCall.canUseFullScreenIntent().catch(() => ({ granted: true }));
    if (granted) return;
    const askedAt = Number(localStorage.getItem(LOCK_SCREEN_HINT_KEY) || 0);
    if (Date.now() - askedAt < LOCK_SCREEN_HINT_EVERY_MS) return;
    localStorage.setItem(LOCK_SCREEN_HINT_KEY, String(Date.now()));
    const alert = await this.alertController.create({
      header: 'Дзвінки на заблокованому екрані',
      message: 'Щоб відповідати на дзвінки, коли телефон заблоковано, дозвольте застосунку показувати екран дзвінка. ' +
        'У налаштуваннях увімкніть «Сповіщення на весь екран» (або «Повноекранні сповіщення»).',
      buttons: [
        { text: 'Пізніше', role: 'cancel' },
        { text: 'Відкрити налаштування', handler: () => { VoipCall.openFullScreenIntentSettings().catch(() => {}); } }
      ]
    });
    await alert.present();
  }

  // Вимкнули «Приймати дзвінки в застосунку» — бекенд більше не будить цей пристрій дзвінками
  async stop(): Promise<void> {
    if (!isVoipCallAvailable()) return;
    const { token } = await VoipCall.getToken().catch(() => ({ token: '' }));
    if (token) this.asteriskService.removeSoftphonePushToken(token).subscribe({ error: () => {} });
    this.savedToken = null;
    this.started = false;
  }

  // fallback — channelId із самої події (старі збірки зберігали відповідь лише до підписки JS)
  private async takePendingAnswer(fallback?: string): Promise<void> {
    const pending = await VoipCall.getPendingAnswer().catch(() => ({ channelId: undefined }));
    const channelId = pending.channelId || fallback;
    if (channelId) diagLog(`pending answer ${channelId} (fallback=${fallback ?? '-'}, answered=${this.answeredChannelId ?? '-'})`);
    if (channelId && channelId !== this.answeredChannelId) this.answer(channelId);
  }

  private heartbeat(): void {
    if (!this.savedToken || !this.softphone.isActive) return;
    this.asteriskService.softphoneHeartbeat(this.savedToken).subscribe({ error: () => {} });
  }

  private saveToken(platform: 'ios' | 'android', token: string): void {
    if (!token || !this.softphone.isActive || token === this.savedToken) return;
    this.savedToken = token;
    this.asteriskService.saveSoftphonePushToken(platform, token).subscribe({ error: () => this.savedToken = null });
  }

  // відповідь на дзвінок ще триває (лінія відкривається, можливо з повторною спробою)
  private answering = false;

  private async answer(channelId: string): Promise<void> {
    this.ringing.delete(channelId);
    this.answeredChannelId = channelId;
    // внутрішній дзвінок: коли співрозмовник покладе трубку, застосунок закриє розмову за подією бекенду
    if (channelId.startsWith(INTERNAL_CALL_PREFIX)) this.internalCalls.setActive(channelId);
    const call = (this.store.selectSnapshot(GpAsteriskState.callList) as GpCallModel[] | null)?.find(item => item.id === channelId);
    const internal = this.internalCalls.get(channelId);
    const label = internal ? `${internal.fromName} · ${internal.fromSip}`
      : (call as any)?.client?.name || call?.phone || 'Вхідний дзвінок';
    // дзвінок клієнту з веб-CRM: вам подзвонили, щоб набрати клієнта — у «Недавніх» це вихідний
    diagLog(`answer ${channelId} active=${this.softphone.isActive} status=${this.softphone.state$.value.status}`);
    try {
      // застосунок могли щойно розбудити — спершу підключаємо телефон до АТС
      if (!this.softphone.isActive) await this.softphone.start();
      this.answering = true;
      await this.softphone.withLine(label, () => firstValueFrom(this.asteriskService.answerFromSoftphone(channelId)), false, {
        direction: internal?.kind === 'callback' ? 'outgoing' : 'incoming',
        id: channelId,
        number: internal?.fromSip || call?.phone || '',
        name: internal?.fromName || (call as any)?.client?.name
      });
      diagLog(`answer ${channelId} line ok`);
    } catch (err: any) {
      diagLog(`answer ${channelId} failed: ${err?.error?.message || err?.message || err}`);
      this.answeredChannelId = null;
      VoipCall.endCall({ channelId }).catch(() => {});
      const toast = await this.toastController.create({
        message: err?.error?.message || err?.message || 'Не вдалося прийняти дзвінок',
        color: 'danger', duration: 3500, position: 'top'
      });
      await toast.present();
    } finally {
      this.answering = false;
      // лінію так і не відкрили (дзвінка в застосунку немає) — системний дзвінок теж закриваємо
      if (!this.softphone.state$.value.call && this.answeredChannelId === channelId) {
        VoipCall.endCall({ channelId }).catch(() => {});
        this.answeredChannelId = null;
      }
    }
  }
}
